import type { FC, ReactNode } from 'react';
import { Avatar, Col, Row, Typography } from 'antd';
import { TbMessage2 } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { CommentComposerValue, ResourceActivityResourceType } from '@/types';

import CommentComposer from '../CommentComposer';

interface ContextualCommentComposerProps {
    avatar?: ReactNode;
    disabled?: boolean;
    headerLabel: string;
    icon?: ReactNode;
    metadataDescription?: string;
    onSubmit: (value: CommentComposerValue) => Promise<boolean | void> | boolean | void;
    resourceId: string;
    resourceType: ResourceActivityResourceType;
    submitting?: boolean;
}

const ContextualCommentComposer: FC<ContextualCommentComposerProps> = ({
    avatar,
    disabled,
    headerLabel,
    icon,
    metadataDescription,
    onSubmit,
    resourceId,
    resourceType,
    submitting,
}) => {
    const { token } = useAntDesignTheme();

    return (
        <div
            style={{ boxSizing: 'border-box', maxWidth: 'calc(100vw - 2rem)', width: '25rem' }}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
        >
            <Row
                align='middle'
                style={{
                    background: token.colorFillQuaternary,
                    borderBottom: `1px solid ${token.colorBorderSecondary}`,
                    columnGap: token.marginXS,
                    padding: '0.625rem 0.75rem',
                }}
                wrap={false}
            >
                <Col flex='none' style={{ lineHeight: 0 }}>
                    {avatar ?? (
                        <Avatar
                            icon={icon ?? <TbMessage2 />}
                            shape='square'
                            size={28}
                            style={{
                                background: token.colorBgContainer,
                                border: `1px solid ${token.colorBorder}`,
                                color: token.colorTextTertiary,
                            }}
                        />
                    )}
                </Col>
                <Col flex='auto' style={{ minWidth: 0 }}>
                    <Typography.Text ellipsis style={{ display: 'block', lineHeight: 1.25 }} title={headerLabel}>
                        {headerLabel}
                    </Typography.Text>
                    {metadataDescription && (
                        <Typography.Text
                            ellipsis
                            style={{ color: token.colorTextTertiary, display: 'block', fontSize: '0.75rem' }}
                            title={metadataDescription}
                        >
                            {metadataDescription}
                        </Typography.Text>
                    )}
                </Col>
            </Row>
            <div style={{ boxSizing: 'border-box', padding: '0.75rem', width: '100%' }}>
                <CommentComposer
                    appearance='dropdown'
                    // The composer is opened by an explicit comment command and should be ready for input.
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    disabled={disabled}
                    minHeight='7rem'
                    onSubmit={onSubmit}
                    placeholder='Leave A Comment...'
                    resourceId={resourceId}
                    resourceType={resourceType}
                    submitting={submitting}
                />
            </div>
        </div>
    );
};

export default ContextualCommentComposer;
